import {useTranslation} from '@aboutyou/i18n';
import {styled} from '@linaria/react';
import React from 'react';
import {COLORS} from '../../../constants/colors';
import {Icon, IconSize} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {SliderControlProps} from '../../molecules/HorizontalSlider/types';

export interface SliderPaginationBaseProps
  extends Omit<
    SliderControlProps,
    'currentSlide' | 'maxPages' | 'scrollToNextItem' | 'scrollToPrevItem'
  > {
  className?: string;
  theme?: {
    iconSize?: keyof typeof IconSize;
    borderColor: keyof typeof COLORS;
    iconColorEnabled: keyof typeof COLORS;
    iconColorDisabled: keyof typeof COLORS;
  };
}

export const SliderPaginationBase: React.FC<
  React.PropsWithChildren<SliderPaginationBaseProps>
> = ({
  className,
  children,
  nextIsActive,
  prevIsActive,
  next,
  prev,
  theme = {
    iconSize: 'XS',
    borderColor: 'Fill.Grey.Primary',
    iconColorEnabled: 'Black',
    iconColorDisabled: 'Fill.Grey.Inactive',
  },
}) => {
  const {t} = useTranslation();
  return (
    <Wrapper
      className={className}
      data-has-content={Boolean(children)}
      data-icon-size={theme.iconSize}
      $borderColor={COLORS[theme.borderColor]}
    >
      <PrevButton
        onClick={prev}
        disabled={!prevIsActive}
        data-testid="sliderPaginationPrevButton"
        aria-label={t('Go to the previous slide')}
        tabIndex={-1}
        $borderColor={COLORS[theme.borderColor]}
      >
        <Icon
          size={theme.iconSize ?? 'XS'}
          asset={GenericIconName.ArrowLeft}
          fill={
            prevIsActive
              ? COLORS[theme.iconColorEnabled]
              : COLORS[theme.iconColorDisabled]
          }
        />
      </PrevButton>
      {children}
      <NextButton
        onClick={next}
        disabled={!nextIsActive}
        data-testid="sliderPaginationNextButton"
        aria-label={t('Go to the next slide')}
        tabIndex={-1}
        $borderColor={COLORS[theme.borderColor]}
      >
        <Icon
          size={theme.iconSize ?? 'XS'}
          asset={GenericIconName.ArrowRight}
          fill={
            nextIsActive
              ? COLORS[theme.iconColorEnabled]
              : COLORS[theme.iconColorDisabled]
          }
        />
      </NextButton>
    </Wrapper>
  );
};

const Wrapper = styled.div<{$borderColor: string}>`
  display: flex;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.4);
  border: ${props =>
    `1px solid ${props.$borderColor ?? COLORS['Fill.Grey.Primary']}`};
  border-radius: 24px;
  width: auto;
  height: 36px;
  margin: 24px auto 0 auto;
  overflow: hidden;

  &[data-has-content='true'] {
    width: 147px;
  }

  &[data-icon-size='XSS'] {
    height: 24px;
  }
`;

const buttonStyles = `
  padding: 10px;
  border: none;
  background: none;

  &[disabled] {
    cursor: auto;
  }

  [data-icon-size='XSS'] & {
    padding: 5px;
  }
`;

const PrevButton = styled.button<{$borderColor: string}>`
  ${buttonStyles};

  border-right: ${props =>
    `1px solid ${props.$borderColor ?? COLORS['Outline.Grey']}`};
`;

const NextButton = styled.button<{$borderColor: string}>`
  ${buttonStyles};

  [data-has-content='true'] & {
    border-left: ${props =>
      `1px solid ${props.$borderColor ?? COLORS['Fill.Grey.Primary']}`};
  }
`;
